import { IndustryTypeLookup } from 'src/app/model/ai-agent';

/** Default industry filter for the inline template step after agent type is selected. */
export const DEFAULT_INLINE_TEMPLATE_INDUSTRY = 'Automotive';
/**
 * Normalizes industry lookup rows from GET /api/Lookup/type?type=IndustryType&isEnum=false.
 *
 * @param rows raw lookup rows from the API
 */
export function normalizeIndustryTypeLookups(rows: unknown[] | null | undefined): IndustryTypeLookup[] {
  const normalized: IndustryTypeLookup[] = [];

  for (const raw of rows ?? []) {
    const row = raw as Record<string, unknown>;
    const name = String(row['name'] ?? row['Name'] ?? row['lookupName'] ?? row['LookupName'] ?? '').trim();
    const description = String(row['description'] ?? row['Description'] ?? name).trim();
    const id = Number(row['id'] ?? row['Id']);

    if (!name) {
      continue;
    }

    normalized.push({
      id: Number.isFinite(id) ? id : 0,
      name,
      description: description || name
    });
  }

  return normalized;
}

/**
 * Orders industry lookup rows for dropdown display (ascending id; "Others" last when id is highest).
 *
 * @param industries normalized industry lookup rows
 */
export function sortIndustryTypeLookupsById(industries: IndustryTypeLookup[]): IndustryTypeLookup[] {
  return [ ...industries ].sort((a, b) => a.id - b.id);
}

/**
 * Resolves the industry query param for prompt-template browse filters.
 *
 * @param industry normalized industry lookup row
 */
export function getIndustryTypeFilterValue(industry: IndustryTypeLookup): string {
  return industry.name;
}

/**
 * @param industries normalized industry lookup rows
 * @param industryId optional industry id from signed-in user details
 * @returns the user's industry filter when resolvable, otherwise Automotive
 */
export function getDefaultInlineTemplateIndustryFilterValue(
  industries: IndustryTypeLookup[],
  industryId?: number | string | null,
  templateIndustryNames: string[] = []
): string {
  const resolvedIndustryId = resolveSignupIndustryId(industries, industryId, templateIndustryNames);
  const resolvedIndustry = findIndustryTypeById(industries, resolvedIndustryId);
  if (resolvedIndustry) {
    return getIndustryTypeFilterValue(resolvedIndustry);
  }

  const match = findAutomotiveIndustry(industries);
  return match ? getIndustryTypeFilterValue(match) : DEFAULT_INLINE_TEMPLATE_INDUSTRY;
}

/**
 * @param industries normalized industry lookup rows
 * @param industryId industry id from signed-in user details
 * @returns the matching industry row when present
 */
export function findIndustryTypeById(
  industries: IndustryTypeLookup[],
  industryId: number | string | null | undefined
): IndustryTypeLookup | null {
  if (industryId == null || !Number.isFinite(Number(industryId))) {
    return null;
  }

  const id = Number(industryId);
  return industries.find((row) => Number(row.id) === id) ?? null;
}

/**
 * @param industries normalized industry lookup rows
 * @returns the Automotive industry row when present
 */
export function findAutomotiveIndustry(industries: IndustryTypeLookup[]): IndustryTypeLookup | null {
  return (
    industries.find(
      (row) => getIndustryTypeFilterValue(row).toLowerCase() === DEFAULT_INLINE_TEMPLATE_INDUSTRY.toLowerCase()
    ) ?? null
  );
}

/**
 * @param industry normalized industry lookup row
 * @returns true when the row represents the catch-all Other/Others option
 */
export function isOthersIndustry(industry: IndustryTypeLookup | null | undefined): boolean {
  const name = industry?.name?.trim().toLowerCase() ?? '';
  return name === 'others' || name === 'other';
}

/**
 * @param industry normalized industry lookup row
 * @param templateIndustryNames industry labels present on prompt template rows
 * @returns true when at least one template exists for the industry name
 */
export function industryNameHasTemplates(
  industry: IndustryTypeLookup,
  templateIndustryNames: string[]
): boolean {
  const name = getIndustryTypeFilterValue(industry).toLowerCase();
  return templateIndustryNames
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean)
    .includes(name);
}

/**
 * @param industries normalized industry lookup rows
 * @param templateIndustryNames industry labels present on prompt template rows
 * @returns Automotive when it has templates, otherwise the first industry with templates
 */
export function findFallbackIndustryWithTemplates(
  industries: IndustryTypeLookup[],
  templateIndustryNames: string[]
): IndustryTypeLookup | null {
  const industriesWithTemplates = filterIndustriesWithTemplates(industries, templateIndustryNames);
  if (!industriesWithTemplates.length) {
    return findAutomotiveIndustry(industries);
  }

  return findAutomotiveIndustry(industriesWithTemplates) ?? industriesWithTemplates[0];
}

/**
 * Resolves the industry id to register when the selected industry has no templates.
 * Other/Others and industries without prompt templates fall back to Automotive when available.
 *
 * @param industries normalized industry lookup rows
 * @param selectedIndustryId industry id chosen in the signup form
 * @param templateIndustryNames industry labels present on prompt template rows
 */
export function resolveSignupIndustryId(
  industries: IndustryTypeLookup[],
  selectedIndustryId: number | string | null | undefined,
  templateIndustryNames: string[] = []
): number {
  const normalizedSelectedId = Number(selectedIndustryId);
  const selected = findIndustryTypeById(industries, selectedIndustryId);
  const hasTemplateCatalog = templateIndustryNames.some((name) => !!name?.trim());
  const fallback = findFallbackIndustryWithTemplates(industries, templateIndustryNames)
    ?? findAutomotiveIndustry(industries);

  if (!selected) {
    return fallback?.id ?? normalizedSelectedId;
  }

  const shouldUseFallback =
    isOthersIndustry(selected)
    || (hasTemplateCatalog && !industryNameHasTemplates(selected, templateIndustryNames));

  if (!shouldUseFallback) {
    return selected.id;
  }

  return fallback?.id ?? selected.id;
}

/**
 * Keeps only lookup industries that appear on at least one prompt template row.
 *
 * @param industryTypeList full industry lookup list
 * @param templateIndustryValues industry values from prompt template API rows
 */
export function filterIndustriesWithTemplates(
  industryTypeList: IndustryTypeLookup[],
  templateIndustryValues: string[]
): IndustryTypeLookup[] {
  const industriesInTemplates = new Set(
    templateIndustryValues.map((value) => value.trim().toLowerCase()).filter(Boolean)
  );

  if (!industriesInTemplates.size) {
    return [];
  }

  return industryTypeList.filter((industry) =>
    industriesInTemplates.has(getIndustryTypeFilterValue(industry).toLowerCase())
  );
}
